import {Vue, Component} from 'vue-property-decorator';
import {IMissedCallCachedData, STATUS_ACTIVE} from "../../../../models/common/cached-data.model";
import {LocalforageWrapper} from "../../../../utils/localforage-wrapper";
import EventBus from "../../../../utils/event-bus";
import {EVENT_BUS, ROUTER} from "../../../../common/constants";
import {formatPhoneNumberForDisplay} from "../../../../utils/util";
import {formatMissedCallTime, getMissedCallCompanyLine, getMissedCallNameLine, withCallerFields} from "../../../../utils/incoming-call-caller";
import {CommonServices} from "../../../../services/common.services";

// Module-scoped so the chain serializes across component instances too: the cached
// list is global, and an in-flight update queued by a destroyed instance must not
// interleave with writes from a remounted one.
const MISSED_CALL_CACHE_KEY = 'Key';
let missedCallCacheQueue: Promise<void> = Promise.resolve();
// The instance currently on screen: queue links mirror the written list here, so an
// update enqueued by a since-destroyed instance still refreshes the live component.
let activeInstance: MissedIncomingCall | null = null;

@Component({
    name: 'MissedIncomingCall',
    computed: {
        STATUS_ACTIVE() {
            return STATUS_ACTIVE
        }
    },
    template: require('./template.html')
})
export default class MissedIncomingCall extends Vue {
    data: IMissedCallCachedData[] = []
    isShowMissedCall: boolean = true

    private commonService = new CommonServices();

    get activeCount(): number {
        return this.data.filter(item => item.is_active === STATUS_ACTIVE.ACTIVE).length;
    }

    get rows() {
        return this.data.map(item => ({
            item,
            name: getMissedCallNameLine(item),
            company: getMissedCallCompanyLine(item),
            time: formatMissedCallTime(item.time, value => this.$formatDateTimeByLocale(value).toString())
        }));
    }

    // All cache writes are read-modify-write; serialize them so overlapping updates
    // (new call event, person-info merge, mark-as-seen) don't lose each other's changes.
    // Without a mutate it is a read-only link: it just mirrors the current list.
    private queueCacheUpdate(mutate?: (list: IMissedCallCachedData[]) => IMissedCallCachedData[]): Promise<void> {
        missedCallCacheQueue = missedCallCacheQueue.then(async () => {
            const latest = (await LocalforageWrapper.getItem(MISSED_CALL_CACHE_KEY) as IMissedCallCachedData[]) || [];
            const next = mutate ? mutate(latest) : latest;
            if (mutate)
                await LocalforageWrapper.setItem(MISSED_CALL_CACHE_KEY, next);
            activeInstance?.handleSortFollowStatus(next);
        }).catch(err => console.error('Missed-call cache update failed:', err));
        return missedCallCacheQueue;
    }

    private handleSortFollowStatus(arr : Array<IMissedCallCachedData>) {
        this.data = arr.sort((a,b) => b.is_active - a.is_active)
    }

    public async handleClearAll() {
        await this.queueCacheUpdate(() => [])
    }

    public async handleClickOneItemMissedCall(item: IMissedCallCachedData) {
        const phone = item.phone;
        await this.queueCacheUpdate(list => list.map(missedCall => {
            if (missedCall.phone === phone) {
                missedCall.is_active = STATUS_ACTIVE.NOT_ACTIVE;
            }
            return missedCall;
        }));
    }

    public handleClickPersonName(event: Event, item: IMissedCallCachedData) {
        if (item.companyId) {
            event.stopPropagation();
            this.handleClickOneItemMissedCall(item);
            this.$router.push({
                name: ROUTER.COMPANY.VIEW,
                params: { id: item.companyId.toString() }
            });
        } else if (item.personId) {
            event.stopPropagation();
            this.handleClickOneItemMissedCall(item);
            this.$router.push({
                name: ROUTER.CLIENT.CLIENT_VIEW,
                params: { id: item.personId.toString() }
            });
        }
    }

    public formatPhone(phone: string): string {
        return formatPhoneNumberForDisplay(phone);
    }

    private async fetchAndUpdatePersonInfo(phoneNumber: string) {
        try {
            const personData = await this.commonService.getPersonInfoByPhoneNumber(phoneNumber);
            if (personData && personData.Person) {
                await this.queueCacheUpdate(list => list.map(item => {
                    if (item.phone === phoneNumber && !item.personId && !item.companyId) {
                        return withCallerFields(item, personData.Person);
                    }
                    return item;
                }));
            }
        } catch (err) {
            console.log('Failed to fetch person info for missed call:', err);
        }
    }

    private onIncomingCall = async (payload) => {
        const incomingCall: IMissedCallCachedData = {
            phone: payload.phoneNumber,
            name: '',
            time: new Date().toISOString(),
            is_active: STATUS_ACTIVE.ACTIVE,
        }
        await this.queueCacheUpdate(list => [incomingCall, ...list])

        // Fetch person info and update the cached entry
        this.fetchAndUpdatePersonInfo(payload.phoneNumber);
    }

    mounted() {
        activeInstance = this;
        // Load as a queue link so the initial read can't interleave with a write
        // still draining from before the remount.
        this.queueCacheUpdate()
        EventBus.$on(EVENT_BUS.NOTIFICATION.NOTIFICATION_LOG_INCOMING_CALL, this.onIncomingCall)
    }

    beforeDestroy() {
        if (activeInstance === this) {
            activeInstance = null;
        }
        // $off needs the same handler reference; without it a header remount would
        // register a second listener and log every call twice.
        EventBus.$off(EVENT_BUS.NOTIFICATION.NOTIFICATION_LOG_INCOMING_CALL, this.onIncomingCall)
    }
}
